import { useTranslation } from "react-i18next";
import { Building2, User, Mail, Phone, Smartphone, Briefcase } from "lucide-react";

interface ContactData {
  company?: {
    name?: string;
    postal_code?: string;
    city?: string;
    street?: string;
    phone?: string;
    email?: string;
    note?: string;
  };
  contacts?: Array<{
    name?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    position?: string;
  }>;
}

interface ContactRevealCardProps {
  contactReveal?: {
    contact_snapshot: Record<string, any>;
    revealed_at: string;
  } | null;
  requesterEmail?: string;
  requesterCompany?: string | null;
  requesterPhone?: string | null;
  contactData?: ContactData | null;
}

const ContactRevealCard = ({ contactReveal, requesterEmail, requesterCompany, requesterPhone, contactData }: ContactRevealCardProps) => {
  const { t } = useTranslation();
  const email = contactReveal?.contact_snapshot?.email || requesterEmail;
  const company = contactReveal?.contact_snapshot?.company || requesterCompany;
  const phone = contactReveal?.contact_snapshot?.phone || requesterPhone;

  // If we have structured contact_data, render it
  if (contactData?.company || (contactData?.contacts && contactData.contacts.length > 0)) {
    const co = contactData.company;
    return (
      <div className="space-y-4">
        {/* Company info */}
        {co && (co.name || co.phone || co.email) && (
          <div className="p-4 bg-accent/5 border border-accent/20 space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-accent" />
              <span className="text-micro uppercase tracking-wider">{t("inquiry.companyInfoTitle", "Unternehmensinformationen")}</span>
            </div>
            {co.name && <p className="text-body font-medium">{co.name}</p>}
            {co.street && <p className="text-caption">{co.street}</p>}
            {(co.postal_code || co.city) && (
              <p className="text-caption">{[co.postal_code, co.city].filter(Boolean).join(" ")}</p>
            )}
            {co.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <a href={`tel:${co.phone}`} className="text-caption hover:text-foreground transition-colors">{co.phone}</a>
              </div>
            )}
            {co.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                <a href={`mailto:${co.email}`} className="text-caption hover:text-foreground transition-colors">{co.email}</a>
              </div>
            )}
            {co.note && <p className="text-caption text-muted-foreground mt-2">{co.note}</p>}
          </div>
        )}

        {/* Contact persons */}
        {contactData.contacts?.map((contact, i) => (
          <div key={i} className="p-4 bg-accent/5 border border-accent/20 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-accent" />
              <span className="text-micro uppercase tracking-wider">
                {i === 0 ? t("inquiry.contactPersonTitle", "Ansprechpartner") : t("inquiry.additionalContact", "Weiterer Ansprechpartner")}
              </span>
            </div>
            {contact.name && <p className="text-body font-medium">{contact.name}</p>}
            {contact.position && (
              <div className="flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-caption">{contact.position}</p>
              </div>
            )}
            {contact.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                <a href={`mailto:${contact.email}`} className="text-caption hover:text-foreground transition-colors">{contact.email}</a>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <a href={`tel:${contact.phone}`} className="text-caption hover:text-foreground transition-colors">{contact.phone}</a>
              </div>
            )}
            {contact.mobile && (
              <div className="flex items-center gap-2">
                <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />
                <a href={`tel:${contact.mobile}`} className="text-caption hover:text-foreground transition-colors">{contact.mobile}</a>
              </div>
            )}
          </div>
        ))}

        {contactReveal?.revealed_at && (
          <p className="text-micro text-muted-foreground">
            {t("contactReveal.revealedAt", "Freigeschaltet am")} {new Date(contactReveal.revealed_at).toLocaleDateString("de-DE")}
          </p>
        )}
      </div>
    );
  }

  // Fallback: legacy display
  if (!email && !company && !phone) return null;

  return (
    <div className="p-4 bg-accent/5 border border-accent/20 space-y-2">
      <span className="text-micro block mb-2">{t("contactReveal.title", "Kontaktdaten")}</span>
      {company && <p className="text-body font-medium">{company}</p>}
      {email && (
        <div className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-muted-foreground" />
          <a href={`mailto:${email}`} className="text-caption hover:text-foreground transition-colors">{email}</a>
        </div>
      )}
      {phone && (
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-muted-foreground" />
          <a href={`tel:${phone}`} className="text-caption hover:text-foreground transition-colors">{phone}</a>
        </div>
      )}
      {contactReveal?.revealed_at && (
        <p className="text-micro mt-2">
          {t("contactReveal.revealedAt", "Freigeschaltet am")} {new Date(contactReveal.revealed_at).toLocaleDateString("de-DE")}
        </p>
      )}
    </div>
  );
};

export default ContactRevealCard;
